export type EnvironmentId = 'localhost' | 'eilat' | 'staging01' | 'production';

export type EnvironmentConfig = {
  id: EnvironmentId;
  label: string;
  payoutApiHost: string;
  portalHost: string;
  live?: boolean;
};

export const ENVIRONMENTS: Record<EnvironmentId, EnvironmentConfig> = {
  localhost: {
    id: 'localhost',
    label: 'Local',
    payoutApiHost: 'http://localhost:3000',
    portalHost: 'http://localhost:3001',
  },
  eilat: {
    id: 'eilat',
    label: 'Eilat (edge)',
    payoutApiHost: 'https://partnerships-api.eilat.melioservices.com',
    portalHost: 'https://developers-portal-api.eilat.melioservices.com',
  },
  staging01: {
    id: 'staging01',
    label: 'staging01',
    payoutApiHost: 'https://api.staging01.melio.com',
    portalHost: 'https://developers-portal-api.staging01.melioservices.com',
  },
  production: {
    id: 'production',
    label: 'Production',
    payoutApiHost: 'https://api.melio.com',
    portalHost: 'https://developers-portal-api.melio.com',
    live: true,
  },
};

export const ENVIRONMENT_IDS = Object.keys(ENVIRONMENTS) as EnvironmentId[];

export const isEnvironmentId = (value: unknown): value is EnvironmentId => typeof value === 'string' && value in ENVIRONMENTS;

export const defaultEnvironmentForHost = (hostname: string): EnvironmentId => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost';
  if (hostname.includes('staging01')) return 'staging01';
  if (hostname.includes('eilat')) return 'eilat';
  if (hostname.includes('melio.com')) return 'production';
  return 'eilat';
};

export const defaultEnvironment = (stackEnv: string | undefined, hostname: string): EnvironmentId => {
  if (isEnvironmentId(stackEnv)) return stackEnv;
  if (stackEnv === 'development') return 'localhost';
  return defaultEnvironmentForHost(hostname);
};

export const LOCAL_ADMIN_TOKEN = 'local-payouts-admin-token';

export const apiBaseUrl = (env: EnvironmentId): string => `${ENVIRONMENTS[env].payoutApiHost}/v2`;

export const portalBaseUrl = (env: EnvironmentId): string => ENVIRONMENTS[env].portalHost;

const TBT_KEY_PROD = 'key_VygUcaXQL4NtSUMxpKxjS';
const TBT_KEY_TEST = 'key_GZFc6KNn6zbN6Y54aFZfpV';

export const tbtConfig = (env: EnvironmentId): { apiKey: string; environment: 'test' | 'production' } => ({
  apiKey: env === 'production' ? TBT_KEY_PROD : TBT_KEY_TEST,
  environment: env === 'production' ? 'production' : 'test',
});

export type SonarSdkConfig = {
  scriptUrl: string;
};

const EILAT_SONAR: SonarSdkConfig = { scriptUrl: 'https://sonar.eilat.melio.com/v1/sonar-sdk.js' };
const STAGING01_SONAR: SonarSdkConfig = { scriptUrl: 'https://sonar.staging01.melio.com/v1/sonar-sdk.js' };
const PRODUCTION_SONAR: SonarSdkConfig = { scriptUrl: 'https://sonar.melio.com/v1/sonar-sdk.js' };

const SONAR_SDK_BY_ENV: Partial<Record<EnvironmentId, SonarSdkConfig>> = {
  localhost: STAGING01_SONAR,
  eilat: EILAT_SONAR,
  staging01: STAGING01_SONAR,
  production: PRODUCTION_SONAR,
};

export const sonarSdkConfig = (env: EnvironmentId): SonarSdkConfig | null => SONAR_SDK_BY_ENV[env] ?? null;
